<template>
  <section
    class="relative bg-blue-50 dark:bg-gray-900 pt-4 pb-6 lg:pt-6 lg:pb-8 overflow-hidden border-b border-gray-200 dark:border-gray-700"
  >
    <div
      class="absolute inset-0 opacity-[0.02] pointer-events-none overflow-hidden flex flex-wrap content-start"
    >
      <svg
        v-for="i in 50"
        :key="i"
        class="w-10 h-10 text-[#293146] dark:text-gray-700 m-2"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        ></path>
      </svg>
    </div>

    <div class="relative max-w-screen-xl mx-auto px-2 sm:px-3 z-10 text-center">
      <template v-if="isLoading">
        <div
          class="h-8 w-56 max-w-full mx-auto bg-gray-200 dark:bg-gray-700 rounded-lg shimmer mb-1.5"
        ></div>
        <div
          class="h-4 w-72 max-w-full mx-auto bg-gray-200 dark:bg-gray-700 rounded-lg shimmer mb-2"
        ></div>
      </template>
      <template v-else>
        <h1
          class="mb-1.5 text-xl sm:text-2xl lg:text-3xl font-bold text-[#293146] dark:text-white text-center"
        >
          {{ $t('home.heroTitle') }}
        </h1>
        <p
          class="mb-2.5 text-sm sm:text-base text-gray-600 dark:text-gray-300 text-center max-w-xl mx-auto"
        >
          {{ $t('home.heroSubtitle') }}
        </p>
      </template>

      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-lg border border-gray-100 dark:border-gray-700 p-2.5 sm:p-3 max-w-4xl mx-auto text-left relative z-20"
      >
        <template v-if="isLoading">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg shimmer"></div>
            <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg shimmer"></div>
            <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg shimmer"></div>
          </div>
          <div class="flex flex-col md:flex-row md:items-center justify-between mt-3">
            <div class="flex flex-wrap gap-1 mb-2 md:mb-0">
              <div class="h-5 w-12 bg-gray-200 dark:bg-gray-700 rounded-full shimmer"></div>
              <div class="h-5 w-14 bg-gray-200 dark:bg-gray-700 rounded-full shimmer"></div>
              <div class="h-5 w-12 bg-gray-200 dark:bg-gray-700 rounded-full shimmer"></div>
            </div>
            <div class="h-8 w-full md:w-20 bg-gray-200 dark:bg-gray-700 rounded-lg shimmer"></div>
          </div>
        </template>
        <template v-else>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            <div class="relative col-span-2 md:col-span-1">
              <div class="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                <svg
                  class="w-3.5 h-3.5 text-gray-400 dark:text-gray-300"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                  />
                </svg>
              </div>
              <input
                type="text"
                class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#C5B47A] dark:focus:ring-[#C5B47A]/40 focus:border-[#C5B47A] dark:focus:border-[#C5B47A] block w-full pl-8 p-2"
                :placeholder="$t('home.searchPlaceholder')"
                v-model="searchQuery"
                @keyup.enter="applySearch"
              />
            </div>

            <div class="relative">
              <div class="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                <svg
                  class="w-3.5 h-3.5 text-gray-400 dark:text-gray-300"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <select
                class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#C5B47A] dark:focus:ring-[#C5B47A]/40 focus:border-[#C5B47A] dark:focus:border-[#C5B47A] block w-full pl-8 p-2 appearance-none"
                v-model="locationFilter"
              >
                <option value="">
                  {{ $t('home.allLocations') }}
                </option>
                <option value="Batanghari">
                  {{ $t('home.locations.batanghari') }}
                </option>
                <option value="Bungo">
                  {{ $t('home.locations.bungo') }}
                </option>
                <option value="Kerinci">
                  {{ $t('home.locations.kerinci') }}
                </option>
                <option value="Merangin">
                  {{ $t('home.locations.merangin') }}
                </option>
                <option value="Muaro Jambi">
                  {{ $t('home.locations.muaroJambi') }}
                </option>
                <option value="Sarolangun">
                  {{ $t('home.locations.sarolangun') }}
                </option>
                <option value="Tanjung Jabung Barat">
                  {{ $t('home.locations.tanjungJabungBarat') }}
                </option>
                <option value="Tanjung Jabung Timur">
                  {{ $t('home.locations.tanjungJabungTimur') }}
                </option>
                <option value="Tebo">
                  {{ $t('home.locations.tebo') }}
                </option>
                <option value="Kota Jambi">
                  {{ $t('home.locations.kotaJambi') }}
                </option>
                <option value="Kota Sungai Penuh">
                  {{ $t('home.locations.kotaSungaiPenuh') }}
                </option>
                <option value="Sengeti">
                  {{ $t('home.locations.sengeti') }}
                </option>
                <option value="Bahar">
                  {{ $t('home.locations.bahar') }}
                </option>
                <option value="Singkut">
                  {{ $t('home.locations.singkut') }}
                </option>
              </select>
            </div>

            <div class="relative">
              <div class="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                <svg
                  class="w-3.5 h-3.5 text-gray-400 dark:text-gray-300"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 14l9-5-9-5-9 5 9 5zm0 0v6"
                  />
                </svg>
              </div>
              <select
                class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#C5B47A] dark:focus:ring-[#C5B47A]/40 focus:border-[#C5B47A] dark:focus:border-[#C5B47A] block w-full pl-8 p-2 appearance-none"
                v-model="educationFilter"
              >
                <option value="">
                  {{ $t('home.allEducation') }}
                </option>
                <option value="SMA">
                  {{ $t('home.sma') }}
                </option>
                <option value="S1">{{ $t('home.s1') }}</option>
                <option value="S2">{{ $t('home.s2') }}</option>
              </select>
            </div>
          </div>

          <div class="flex flex-col md:flex-row md:items-center justify-between mt-3">
            <div class="hidden md:flex flex-wrap gap-1 mb-2 md:mb-0 pr-1">
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === '',
                }"
                @click="selectedExp = ''"
              >
                {{ $t('home.allExp') }}
              </span>
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === 'Tanpa Pengalaman',
                }"
                @click="selectedExp = 'Tanpa Pengalaman'"
              >
                {{ $t('home.noExperience') }}
              </span>
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === '< 1 Tahun',
                }"
                @click="selectedExp = '< 1 Tahun'"
              >
                {{ $t('home.expUnder1') }}
              </span>
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === '1-2 Tahun',
                }"
                @click="selectedExp = '1-2 Tahun'"
              >
                {{ $t('home.exp1to2') }}
              </span>
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === '3-5 Tahun',
                }"
                @click="selectedExp = '3-5 Tahun'"
              >
                {{ $t('home.exp3to5') }}
              </span>
              <span
                class="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                :class="{
                  'bg-gray-700 dark:bg-[#C5B47A] text-white dark:text-gray-900 border-gray-700 dark:border-[#C5B47A] hover:bg-gray-800 dark:hover:bg-[#C5B47A]':
                    selectedExp === '> 5 Tahun',
                }"
                @click="selectedExp = '> 5 Tahun'"
              >
                {{ $t('home.expMoreThan5') }}
              </span>
            </div>

            <button
              type="button"
              class="text-white dark:text-gray-900 bg-[#293146] dark:bg-[#C5B47A] hover:bg-[#111c30] dark:hover:bg-[#C5B47A] font-medium rounded-lg text-sm px-4 py-1.5 text-center shrink-0 w-full md:w-auto disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              @click="applySearch"
              :disabled="isSearching"
            >
              <svg
                v-if="isSearching"
                class="w-3.5 h-3.5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ $t('home.searchBtn') }}</span>
            </button>
            <router-link
              v-if="cvEnabled"
              to="/cv"
              @click="gtagEvent('click_cv_cta', { source: 'hero_section' })"
              class="text-[#C5B47A] dark:text-[#C5B47A] hover:text-white dark:hover:text-gray-900 border-2 border-[#C5B47A] dark:border-[#C5B47A] hover:bg-[#C5B47A] dark:hover:bg-[#C5B47A] font-medium rounded-lg text-sm px-4 py-1.5 text-center shrink-0 w-full md:w-auto flex items-center justify-center gap-1.5"
            >
              <Icon icon="mdi:file-document-edit-outline" class="w-4 h-4" />
              Buat CV Sekarang
            </router-link>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import { event as gtagEvent } from 'vue-gtag';

const props = defineProps({
  initialSearch: { type: String, default: '' },
  initialLocation: { type: String, default: '' },
  initialEducation: { type: String, default: '' },
  initialExperience: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  cvEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(['search']);

const searchQuery = ref(props.initialSearch);
const locationFilter = ref(props.initialLocation);
const educationFilter = ref(props.initialEducation);
const selectedExp = ref(props.initialExperience || '');
const isSearching = ref(false);

watch(
  () => props.initialSearch,
  (val) => (searchQuery.value = val)
);
watch(
  () => props.initialLocation,
  (val) => (locationFilter.value = val)
);
watch(
  () => props.initialEducation,
  (val) => (educationFilter.value = val)
);
watch(
  () => props.initialExperience,
  (val) => (selectedExp.value = val || '')
);
watch(
  () => props.isLoading,
  (val) => {
    if (!val) isSearching.value = false;
  }
);

const applySearch = () => {
  gtagEvent('search', {
    search_term: searchQuery.value || '(empty)',
    location: locationFilter.value || 'All',
    education: educationFilter.value || 'All',
    experience: selectedExp.value || 'All',
  });

  emit('search', {
    search: searchQuery.value,
    location: locationFilter.value,
    education: educationFilter.value,
    experience: selectedExp.value,
  });
};
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer {
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.dark .shimmer {
  background: linear-gradient(90deg, #4b5563 25%, #6b7280 50%, #4b5563 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
</style>
